import React, { useState, useEffect } from 'react';
import { 
  Card, 
  Select, 
  Input, 
  DatePicker, 
  Tag, 
  Empty, 
  Spin,
  Pagination 
} from 'antd';
import { 
  SearchOutlined, 
  CalendarOutlined,
  FilterOutlined 
} from '@ant-design/icons';
import ArchiveItem from '../../components/ArchiveItem';
import { getArchivePosts } from '../../api/mockData';
import './index.css';

const { Option } = Select;
const { Search } = Input;

const Archive = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState('');
  const [selectedYear, setSelectedYear] = useState('all');
  const [selectedTag, setSelectedTag] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [allTags, setAllTags] = useState([]);
  const [years, setYears] = useState([]);

  useEffect(() => {
    fetchPosts();
    extractTagsAndYears();
  }, [currentPage, searchText, selectedYear, selectedTag]);

  const fetchPosts = async () => {
    setLoading(true);
    try {
      const data = await getArchivePosts({
        page: currentPage,
        search: searchText,
        year: selectedYear === 'all' ? null : selectedYear,
        tag: selectedTag === 'all' ? null : selectedTag
      });
      setPosts(data.posts);
      setTotal(data.total);
    } catch (error) {
      console.error('获取归档文章失败:', error);
    } finally {
      setLoading(false);
    }
  };

  const extractTagsAndYears = async () => {
    const data = await getArchivePosts();
    const tags = new Set();
    const years = new Set();
    
    data.posts.forEach(post => {
      post.tags?.forEach(tag => tags.add(tag));
      const year = new Date(post.createdAt).getFullYear();
      years.add(year);
    });
    
    setAllTags(Array.from(tags));
    setYears(Array.from(years).sort((a, b) => b - a));
  };

  const handleSearch = (value) => {
    setSearchText(value);
    setCurrentPage(1);
  };

  const handleYearChange = (value) => {
    setSelectedYear(value);
    setCurrentPage(1);
  };

  const handleTagChange = (value) => {
    setSelectedTag(value);
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const clearFilters = () => {
    setSearchText('');
    setSelectedYear('all');
    setSelectedTag('all');
    setCurrentPage(1);
  };

  return (
    <div className="archive-container">
      <div className="archive-header">
        <h1 className="archive-title">文章归档</h1>
        <p className="archive-subtitle">共 {total} 篇文章</p>
      </div>

      <Card className="filter-card">
        <div className="filter-section">
          <div className="filter-item">
            <Search
              placeholder="搜索文章标题或内容..."
              prefix={<SearchOutlined />}
              onSearch={handleSearch}
              className="search-input"
              allowClear
            />
          </div>
          
          <div className="filter-item">
            <span className="filter-label">
              <CalendarOutlined /> 年份:
            </span>
            <Select
              value={selectedYear}
              onChange={handleYearChange}
              className="filter-select"
            >
              <Option value="all">全部年份</Option>
              {years.map(year => (
                <Option key={year} value={year}>{year}年</Option>
              ))}
            </Select>
          </div>
          
          <div className="filter-item">
            <span className="filter-label">
              <FilterOutlined /> 标签:
            </span>
            <Select
              value={selectedTag}
              onChange={handleTagChange}
              className="filter-select"
            >
              <Option value="all">全部标签</Option>
              {allTags.map(tag => (
                <Option key={tag} value={tag}>
                  <Tag color="blue">{tag}</Tag>
                </Option>
              ))}
            </Select>
          </div>
          
          <div className="filter-item">
            <span 
              className="clear-filters"
              onClick={clearFilters}
            >
              清除筛选
            </span>
          </div>
        </div>
      </Card>

      <div className="archive-content">
        {loading ? (
          <div className="archive-loading">
            <Spin size="large" />
          </div>
        ) : posts.length === 0 ? (
          <Empty
            description={
              searchText || selectedYear !== 'all' || selectedTag !== 'all' 
                ? "没有找到符合条件的文章" 
                : "暂无文章"
            }
            className="archive-empty"
          />
        ) : (
          <>
            <div className="posts-list">
              {posts.map(post => (
                <ArchiveItem key={post.id} post={post} />
              ))}
            </div>
            
            <div className="archive-pagination">
              <Pagination
                current={currentPage}
                total={total}
                pageSize={10}
                onChange={handlePageChange}
                showSizeChanger={false}
                showTotal={(total) => `共 ${total} 条`}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Archive;